<template>
  <div>
    <v-dialog
      v-model="formOpened"
      persistent
      width="40%"
    >
      <v-card>
        <v-card-title>
          <span class="headline">Replace File for: {{existingFile.month}} {{existingFile.year}}</span>
        </v-card-title>
        <v-card-text>

          <v-layout justify-center>
            <v-form ref="replaceFileForm" @submit.prevent style="width: 100%">
              <v-flex xs12>
                <v-layout row wrap>
                  <v-flex xs12>
                    <span>Replacing {{existingFile.file_name}}</span>
                  </v-flex>
                </v-layout>
                <v-layout row>
                  <v-flex xs12>
                    <v-text-field
                      label="Choose source file"
                      @click="$refs.fileInput.click()"
                      v-model="uploadFileName"
                      prepend-icon="attach_file"
                      readonly
                      :error-messages="fileError"
                    ></v-text-field>
                    <input
                      ref="fileInput"
                      type="file"
                      style="display: none"
                      @change="handleFileUpload"
                      accept=".xlsx,.xls,.csv"
                    >
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-form>
          </v-layout>
          
          <!-- Upload progress indicator -->
          <v-layout row v-if="uploadProgress > 0 && uploadProgress < 100">
            <v-flex xs12>
              <p class="text-center mb-0">Processing file... Please wait</p>
              <v-progress-linear
                :value="uploadProgress"
                color="primary"
                height="25"
                striped
              >
                <template v-slot:default>
                  <strong>{{ Math.ceil(uploadProgress) }}%</strong>
                </template>
              </v-progress-linear>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            text
            color="blue darken-1"
            :loading="dataLoading"
            :disabled="dataLoading"
            @click="closeDialog"
          >
            Cancel
          </v-btn>
          <v-btn 
            text
            color="blue darken-1"
            :loading="dataLoading"
            :disabled="dataLoading || !isValid"
            @click="replaceFile"
          >
            Replace File
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <data-loading :visible="dataLoading"></data-loading>

    <v-dialog v-model="generateReportDialog" max-width="500px">
      <v-card>
        <v-card-title class="py-3 px-4" style="background-color: #f5f5f5;">
          <span class="body-2 font-weight-medium">Generate Credit Score Report</span>
        </v-card-title>
        <v-card-text class="pt-4">
          File replaced successfully. Would you like to generate a credit score report now?
        </v-card-text>
        <v-card-actions style="background-color: #f5f5f5;">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="closeGenerateReportDialog">Not Now</v-btn>
          <v-btn text color="primary" @click="generateReport" :loading="generating">Yes, Generate Report</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add snackbar notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
    >
      {{ snackbar.text }}
      <v-btn text @click="snackbar.show = false">Close</v-btn>
    </v-snackbar>

  </div>
</template>

<script>
import creditScoringService from "@/services/creditScoringService";
import ErrorDialog from "@/components/ErrorDialogComponent.vue";
import accessControlMixin from '@/mixins/accessControlMixin';

export default {
  name: "ReplaceFileUploadComponent",
  mixins: [accessControlMixin],
  
  components: {
    ErrorDialog
  },
  
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    existingFile: {
      type: Object,
      required: true
    }
  },
  
  data: () => ({
    dataLoading: false,
    uploadFile: null,
    uploadFileName: '',
    error: {
      show: false,
      message: '',
      details: ''
    },
    errorDialog: {
      show: false,
      title: 'Error',
      message: '',
      details: '',
      type: 'error',
      suggestion: '',
      showRetryButton: false,
      showActionButton: false,
      actionButtonText: 'Action',
      actionButtonColor: 'primary',
      action: null
    },
    fileError: '',
    generateReportDialog: false,
    generating: false,
    uploadResponse: null,
    snackbar: {
      show: false,
      text: '',
      color: 'info'
    },
    uploadProgress: 0,
    progressInterval: null
  }),

  computed: {
    formOpened: {
      get() {
        return this.visible;
      },
      set(newValue) {
        this.$emit("update:visible", newValue);
      }
    },

    isValid() {
      return this.uploadFile && !this.fileError;
    }
  },

  methods: {
    handleFileUpload(event) {
      this.fileError = '';
      const file = event.target.files[0];
      
      if (file) {
        // Validate file type
        const allowedTypes = [
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel',
          'text/csv'
        ];
        
        // Check file extension if MIME type doesn't match
        const fileName = file.name.toLowerCase();
        const validExtension = fileName.endsWith('.xlsx') || 
                             fileName.endsWith('.xls') || 
                             fileName.endsWith('.csv');
        
        if (!allowedTypes.includes(file.type) && !validExtension) {
          this.fileError = 'Please upload an Excel file (.xlsx, .xls) or CSV file';
          this.uploadFile = null;
          this.uploadFileName = '';
          return;
        }

        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          this.fileError = 'File size should be less than 10MB';
          this.uploadFile = null;
          this.uploadFileName = '';
          return;
        }

        this.uploadFile = file;
        this.uploadFileName = file.name;
      }
    },

    showError(message, details = '') {
      this.showErrorDialog({
        title: 'Validation Error',
        message: message,
        details: details,
        type: 'error',
        showRetryButton: false
      });
    },

    showErrorDialog(options) {
      const errorTitle = options.title || 'Error';
      const errorMessage = options.message || 'An error occurred';
      const errorDetails = options.details || '';
      const errorType = options.type || 'error';
      const suggestion = options.suggestion || '';
      const showRetryButton = options.showRetryButton || false;
      const actionButtonText = options.actionButtonText || 'Action';
      const actionButtonColor = options.actionButtonColor || 'primary';
      
      const hasValidAction = typeof options.action === 'function';
      const showActionButton = options.showActionButton && hasValidAction;
      
      this.errorDialog = {
        show: true,
        title: errorTitle,
        message: errorMessage,
        details: errorDetails,
        type: errorType,
        suggestion: suggestion,
        showRetryButton: showRetryButton,
        showActionButton: showActionButton,
        actionButtonText: actionButtonText,
        actionButtonColor: actionButtonColor,
        action: hasValidAction ? options.action : () => {}
      };
      
      if (options.showActionButton && !hasValidAction) {
        console.warn('showActionButton is true but no valid action handler was provided');
      }
    },

    handleDialogAction() {
      if (typeof this.errorDialog.action === 'function') {
        this.errorDialog.action();
      } else {
        console.warn('Action handler called but no action function defined');
      }
    },

    clearError() {
      this.fileError = '';
      this.errorDialog.show = false;
    },

    validateForm() {
      let isValid = true;

      if (!this.uploadFile) {
        this.fileError = 'Please select a file to upload';
        isValid = false;
      }

      return isValid;
    },

    handleApiError(error) {
      console.error('Replace error:', error);
      this.$emit('replace-error', error);
    },

    retryReplace() {
      this.replaceFile();
    },
    
    downloadTemplate() {
      console.log('Downloading template file');
      
      const templateUrl = process.env.VUE_APP_TEMPLATE_URL || '/templates/loan_upload_template.xlsx';
      
      try {
        window.open(templateUrl, '_blank');
      } catch (e) {
        console.error('Failed to open template URL:', e);
        
        const link = document.createElement('a');
        link.href = templateUrl;
        link.download = 'loan_upload_template.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      this.showNotification('Template download initiated', 'info');
    },

    simulateProgressUpdates() {
      // Reset progress
      this.uploadProgress = 0;
      
      // Simulate progress while upload is in progress
      this.progressInterval = setInterval(() => {
        if (this.uploadProgress < 90) {
          // Increment more slowly as we approach 90%
          const increment = this.uploadProgress < 50 ? 5 : 2;
          this.uploadProgress += increment;
        }
      }, 300);
    },

    async replaceFile() {
      if (!this.validateForm()) {
        return;
      }

      this.dataLoading = true;
      this.clearError();
      this.simulateProgressUpdates();

      try {
        const formData = new FormData();
        formData.append("file", this.uploadFile);
        formData.append("upload_user", (process.env.VUE_APP_ENV === "dev") ? 
          "System" : 
          JSON.parse(localStorage.getItem("user")).email
        );

        const result = await creditScoringService.replaceUploadedFile(
          this.existingFile._id, 
          formData
        );

        // Upload complete
        this.uploadProgress = 100;
        clearInterval(this.progressInterval);

        if (result.result) {
          this.uploadResponse = {
            ...result.value,
            uploadId: result.value.uploadId || this.existingFile._id
          }; 
          
          if (this.hasPrivilege('Generate Reports')) {
            this.generateReportDialog = true; // Show the dialog asking to generate report
            return;
          }
          return true;
        } else {
          throw result.error;
        }
      } catch (error) {
        // Clear interval and reset progress on error
        clearInterval(this.progressInterval);
        this.uploadProgress = 0;
        this.handleApiError(error);
        return false;
      } finally {
        this.dataLoading = false;
      }
    },

    closeDialog() {
      this.clearError();
      this.uploadFile = null;
      this.uploadFileName = '';
      this.uploadProgress = 0;
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
      }
      this.formOpened = false;
    },

    closeGenerateReportDialog() {
      this.generateReportDialog = false;
      this.$emit('replace-success', this.uploadResponse);
      this.closeDialog();
    },

    async generateReport() {
      // Use the uploadId from the response or fall back to the existing file ID
      const uploadId = this.uploadResponse.uploadId || this.existingFile._id;
      
      if (!uploadId) {
        console.error('Missing uploadId in response:', this.uploadResponse);
        this.showNotification('Error: Upload ID not available', 'error');
        this.generateReportDialog = false;
        return;
      }

      this.generating = true;
      try {
        
        const response = await creditScoringService.calculateScores(uploadId);
        
        if (response.result) {
          this.showNotification('Credit score report generated successfully', 'success');
          
          // Close the dialog before navigation
          this.generateReportDialog = false;
          
          // Emit event with report data - will trigger navigation in parent component
          this.$emit('report-generated', response.value);
          
          return true;
        } else {
          // Handle 409 error specifically
          if (response.status === 409 && response.data && response.data.existingReportId) {
            const reportId = response.data.existingReportId;
            
            // Only enable the action button if we have a valid reportId
            const hasReportId = !!reportId;
            
            this.showErrorDialog({
              title: 'Report Already Exists',
              message: 'A report already exists for this month and year.',
              type: 'info',
              showRetryButton: false,
              showActionButton: hasReportId,
              actionButtonText: 'View Existing Report',
              actionButtonColor: 'primary',
              action: () => { 
                this.$emit('view-existing-report', reportId);
              }
            });
          } else {
            throw response.error;
          }
        }
      } catch (error) {
        console.error('Report generation error:', error);
        this.handleApiError(error);
        return false;
      } finally {
        this.generating = false;
        this.generateReportDialog = false;
        
        // Emit replace success to close the dialog
        this.$emit('replace-success', this.uploadResponse);
        this.closeDialog();
      }
    },

    showNotification(text, color = 'info') {
      this.snackbar = {
        show: true,
        text,
        color
      };
    }
  },

  watch: {
    visible(newValue) {
      if (!newValue) {
        this.uploadFile = null;
        this.uploadFileName = '';
        this.clearError();
        this.uploadProgress = 0;
        if (this.progressInterval) {
          clearInterval(this.progressInterval);
        }
      }
    }
  },
  
  beforeDestroy() {
    // Clean up interval if component is destroyed
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }
  }
}
</script>

<style scoped>
.error-alert {
  margin-bottom: 16px;
}

.error-title {
  font-weight: 600;
  margin-bottom: 8px;
}

.error-details {
  margin-top: 8px;
  font-size: 0.9em;
}

.v-text-field {
  margin-top: 16px;
}

.headline {
  font-size: 18px !important;
  font-weight: 500;
}

/* Styles for error lists */
:deep(.error-list) {
  margin-top: 8px;
  margin-bottom: 8px;
  padding-left: 24px;
}

:deep(.error-list li) {
  margin-bottom: 4px;
}
</style>